import type { Autonomy, DeskDetail, StageReadiness, TrainingCandidate, TrainingSession, TrainingState } from '@deskworks/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { ArrowDownRight, ArrowUpRight, Check, ChevronRight, CircleDashed, Crown, Gauge, GraduationCap, History, Inbox, ListChecks, MoreHorizontal, Rocket, Tags } from 'lucide-react';
import { useEffect, useState, type ReactNode } from 'react';
import { ConversationThread, effectiveAgentState, failedMessage, useConversationLive, type ConversationLive } from '@/components/chat/conversation-thread';
import { ModelOnboarding } from '@/components/chat/model-onboarding';
import { ChatWritesSwitch } from '@/components/conversations';
import { DeskLearningsSection } from '@/components/desk/desk-learnings';
import { DESK_STAGE_INFO, StageBadge } from '@/components/desk/stage-toggle';
import { Markdown } from '@/components/markdown';
import { Section } from '@/components/section';
import { ErrorState, LoadingRows } from '@/components/states';
import { ConversationStateBadge, WorkItemStatusBadge } from '@/components/status-badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { api } from '@/lib/api';
import { useApiMutation } from '@/lib/hooks';
import { intlLocale, t, tr } from '@/lib/i18n';
import { qk } from '@/lib/query-keys';
import { cn } from '@/lib/utils';

/**
 * The desk's training (docs/features/desk-training.md): a chat with its Lead, which builds the desk up with you on real
 * cases — step by step to Co-Pilot and Autopilot. Beside it how far the desk is, measured by Deskworks.
 */
export function DeskTraining({ desk }: { desk: DeskDetail }) {
  const training = useTraining(desk.id);
  // The training's own answer is the fresher one: a session ends in the chat, before the desk is fetched again.
  const conversationId = training.data ? training.data.conversationId : desk.trainingConversationId;
  if (conversationId) return <TrainingChat desk={desk} conversationId={conversationId} training={training.data} />;
  if (training.isPending) return <LoadingRows rows={4} />;
  if (training.data?.sessions.length) return <TrainingBetween desk={desk} training={training.data} />;
  return <TrainingStart desk={desk} training={training.data} />;
}

export function useTraining(deskId: string) {
  return useQuery({ queryKey: qk.deskTraining(deskId), queryFn: () => api.desks.training(deskId) });
}

/** A desk that has never trained: no session running and none ended. */
export function neverTrained(training: TrainingState | undefined) {
  return !!training && !training.conversationId && training.sessions.length === 0;
}

/** "Let's go" from elsewhere (the setup assistant, the desk overview): starts the training and opens its tab. */
export function useStartTraining(desk: { id: string; workspaceId: string }, lead?: string | null) {
  const navigate = useNavigate();
  return useApiMutation(() => api.desks.startTraining(desk.id), {
    invalidate: [qk.desk(desk.id), qk.deskTraining(desk.id)],
    success: t('{lead} starts the training', { lead: lead ?? t('The Lead') }),
    onSuccess: () => void navigate({ to: '/w/$workspaceId/desks/$deskId', params: { workspaceId: desk.workspaceId, deskId: desk.id }, search: { tab: 'training' } }),
  });
}

/**
 * "Work through in training": a person takes an open Work Item of the desk into the training (docs/features/desk-training.md)
 * — the session starts if none runs, the item rests there, and the Lead works it through with them. Opens the tab.
 */
export function useTrainWorkItem(desk: { id: string; workspaceId: string }, lead?: string | null) {
  const navigate = useNavigate();
  return useApiMutation((workItemId: string) => api.desks.startTraining(desk.id, { workItemId }), {
    invalidate: (_, workItemId) => [qk.desk(desk.id), qk.deskTraining(desk.id), qk.workItem(workItemId)],
    success: t('{lead} works it through with you in the training', { lead: lead ?? t('The Lead') }),
    errorTitle: t('Couldn’t take it into the training'),
    onSuccess: () => void navigate({ to: '/w/$workspaceId/desks/$deskId', params: { workspaceId: desk.workspaceId, deskId: desk.id }, search: { tab: 'training' } }),
  });
}

/** How many open Work Items the list shows before "More". */
const CANDIDATES_SHOWN = 3;

/** Open work of the desk a person can take into the training, one "Work through" away. */
function TrainingCandidates({ desk, candidates, compact }: { desk: DeskDetail; candidates: TrainingCandidate[]; compact?: boolean }) {
  const [all, setAll] = useState(false);
  const train = useTrainWorkItem(desk, desk.leadAgent?.name);
  const shown = all ? candidates : candidates.slice(0, CANDIDATES_SHOWN);
  const disabled = !desk.leadAgent || desk.status !== 'active';
  if (!candidates.length) {
    return <p className="text-xs text-muted-foreground">{t('No open Work Items to train on right now.')}</p>;
  }
  return (
    <div className="flex flex-col gap-2">
      <ul className={cn('flex flex-col', compact ? 'gap-2' : 'divide-y')}>
        {shown.map((c) => (
          <li key={c.id} className={cn('flex flex-wrap items-center gap-x-3 gap-y-2', compact ? 'rounded-md border bg-card p-2.5' : 'py-2.5 first:pt-0 last:pb-0')}>
            <div className="min-w-0 flex-1 basis-48">
              <Link
                to="/w/$workspaceId/work-items/$workItemId"
                params={{ workspaceId: desk.workspaceId, workItemId: c.id }}
                className="line-clamp-2 text-sm font-medium break-words hover:text-primary hover:underline"
              >
                {c.title}
              </Link>
              <div className="mt-1 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                <WorkItemStatusBadge status={c.status} />
                {c.externalReference && <span className="truncate font-mono">{c.externalReference.id}</span>}
              </div>
            </div>
            <Button
              size="sm"
              variant="outline"
              className={cn('shrink-0 max-md:h-10 max-md:px-4', compact && 'w-full')}
              onClick={() => train.mutate(c.id)}
              loading={train.isPending && train.variables === c.id}
              disabled={disabled || train.isPending}
            >
              <GraduationCap /> {t('Work through')}
            </Button>
          </li>
        ))}
      </ul>
      {candidates.length > CANDIDATES_SHOWN && (
        <Button variant="ghost" size="sm" className="self-start max-md:h-10" onClick={() => setAll((v) => !v)}>
          {all ? t('Less') : t('More ({n})', { n: candidates.length - CANDIDATES_SHOWN })}
        </Button>
      )}
    </div>
  );
}

/** The candidates as a card of their own (before a session and between sessions). */
function TrainingCandidatesCard({ desk, candidates }: { desk: DeskDetail; candidates: TrainingCandidate[] }) {
  if (!candidates.length) return null;
  return (
    <Section icon={<Inbox />} title={t('Open work to train on')} description={t('Pick a Work Item, and the Lead works it through with you in the training.')}>
      <TrainingCandidates desk={desk} candidates={candidates} />
    </Section>
  );
}

/**
 * On the overview of a desk that has never trained (docs/features/desk-training.md): the desk is set up but knows
 * nothing yet — its first training is the next step.
 */
export function FirstTrainingCard({ desk }: { desk: DeskDetail }) {
  const training = useTraining(desk.id);
  const lead = desk.leadAgent?.name ?? null;
  const start = useStartTraining(desk, lead);
  if (!neverTrained(training.data) || !desk.leadAgent || desk.status !== 'active') return null;
  return (
    <Card className="flex flex-col gap-4 border-sky-200 bg-sky-50/60 p-5 sm:flex-row sm:items-center dark:border-sky-900 dark:bg-sky-950/30">
      <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300">
        <GraduationCap className="size-5" />
      </div>
      <div className="min-w-0 flex-1">
        <h2 className="text-base font-semibold">{t('This desk hasn’t learned anything yet')}</h2>
        <p className="mt-0.5 text-sm text-muted-foreground">
          {t('Start its first training: {lead} takes real cases with you, you show the way, and it writes down how the work is done here.', { lead: lead! })}
        </p>
      </div>
      <Button onClick={() => start.mutate()} loading={start.isPending} className="shrink-0">
        <Rocket /> {t('Start the first training')}
      </Button>
    </Card>
  );
}

/** Before the first "Let's go": what the training is, and where the desk stands. */
function TrainingStart({ desk, training }: { desk: DeskDetail; training: TrainingState | undefined }) {
  const lead = desk.leadAgent?.name ?? t('the Lead');
  const start = useApiMutation(() => api.desks.startTraining(desk.id), {
    invalidate: [qk.desk(desk.id), qk.deskTraining(desk.id)],
    success: t('{lead} starts the training', { lead }),
  });
  return (
    <div className="flex flex-col gap-6">
      <Card className="overflow-hidden">
        <div className="flex flex-col gap-5 p-6 sm:flex-row sm:items-start">
          <div className="flex size-12 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300">
            <GraduationCap className="size-6" />
          </div>
          <div className="min-w-0 flex-1">
            <h2 className="text-lg font-semibold">{t('Train your desk with {lead}', { lead })}</h2>
            <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
              {t('You talk with the Lead like in a chat. It builds the desk up with you: it takes a real case, you tell it how it is solved, it does it and writes down how — and with every case it needs you less.')}
            </p>
            <ol className="mt-4 grid max-w-3xl gap-3 text-sm sm:grid-cols-3">
              <Step n={1} title={t('Real cases')}>{t('The Lead fetches the work from the desk’s sources and goes through it with you, one case at a time.')}</Step>
              <Step n={2} title={t('You show the way')}>{t('You say how a case is solved; the Lead does it right in the chat and writes the playbook.')}</Step>
              <Step n={3} title={t('More autonomous')}>{t('Once cases need no more corrections, the desk moves to Co-Pilot, then Autopilot.')}</Step>
            </ol>
            <div className="mt-5 flex flex-wrap items-center gap-3">
              <Button size="lg" onClick={() => start.mutate()} loading={start.isPending} disabled={!desk.leadAgent || desk.status !== 'active'}>
                <Rocket /> {t('Let’s go')}
              </Button>
              {!desk.leadAgent ? (
                <span className="text-xs text-amber-700 dark:text-amber-400">{t('Choose a Lead for this desk first (Setup).')}</span>
              ) : desk.status !== 'active' ? (
                <span className="text-xs text-muted-foreground">{t('Resume the desk to train it.')}</span>
              ) : (
                <span className="text-xs text-muted-foreground">{t('{lead} writes first.', { lead })}</span>
              )}
            </div>
          </div>
        </div>
      </Card>
      <div className="grid gap-6 lg:grid-cols-3">
        <div className="flex min-w-0 flex-col gap-6 lg:col-span-2">
          {training && <TrainingCandidatesCard desk={desk} candidates={training.candidates} />}
          <DeskLearningsSection desk={desk} />
        </div>
        <div className="flex min-w-0 flex-col gap-6">
          {training && (
            <Section icon={<Gauge />} title={t('Where the desk stands')}>
              <div className="flex flex-col gap-5">
                <ReadinessBlock readiness={training.readiness} />
                <AutonomyBlock autonomy={training.autonomy} />
              </div>
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

function Step({ n, title, children }: { n: number; title: string; children: ReactNode }) {
  return (
    <li className="rounded-lg border bg-muted/30 p-3">
      <div className="flex items-center gap-2 font-medium">
        <span className="flex size-5 items-center justify-center rounded-full bg-primary text-[11px] text-primary-foreground">{n}</span>
        {title}
      </div>
      <p className="mt-1 text-xs text-muted-foreground">{children}</p>
    </li>
  );
}

/** The training chat with the Lead (every step it takes shown under its replies), and the desk's progress beside it. */
function TrainingChat({ desk, conversationId, training }: { desk: DeskDetail; conversationId: string; training: TrainingState | undefined }) {
  const queryClient = useQueryClient();
  const { query: conv, live } = useConversationLive(conversationId);
  const detail = conv.data ?? null;
  const lead = detail?.agentName ?? desk.leadAgent?.name ?? t('the Lead');
  const state = effectiveAgentState(detail, live.awaitingReply);
  const count = detail?.messages.length ?? 0;

  // What the Lead does in the chat (a case recorded, a plan, a stage) shows beside it right away.
  useEffect(() => {
    if (!count) return;
    void queryClient.invalidateQueries({ queryKey: qk.deskTraining(desk.id) });
    void queryClient.invalidateQueries({ queryKey: qk.deskLearnings(desk.id) });
  }, [count, desk.id, queryClient]);

  const end = useApiMutation(() => api.conversations.update(conversationId, { status: 'archived' }), {
    invalidate: [qk.desk(desk.id), qk.deskTraining(desk.id)],
    success: t('Session ended'),
  });

  return (
    <div className="flex flex-col gap-6">
      {/* On a phone the chat fills the screen between the header and the tab bar; the progress follows below it. */}
      <Card className="flex h-[min(820px,82vh)] min-h-[520px] flex-col overflow-hidden max-md:h-[calc(100dvh-21rem-env(safe-area-inset-top)-env(safe-area-inset-bottom))] max-md:min-h-[440px] lg:flex-row">
        <div className="flex min-h-0 min-w-0 flex-1 flex-col">
          <div className="flex flex-wrap items-center gap-x-3 gap-y-2 border-b px-4 py-3">
            <div className="flex size-8 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300">
              <GraduationCap className="size-4" />
            </div>
            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-semibold">{detail?.title ?? t('Training')}</span>
                {state && <ConversationStateBadge state={state} />}
              </div>
              <div className="flex items-center gap-1 truncate text-xs text-muted-foreground">
                <Crown className="size-3" /> {t('with {lead}', { lead })} · {t('every step it takes shows under its replies')}
              </div>
            </div>
            <div className="flex shrink-0 items-center gap-1">
              {detail && <ChatWritesSwitch conv={detail} />}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" size="icon-sm" aria-label={t('More')}>
                    <MoreHorizontal />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end">
                  <DropdownMenuItem onSelect={() => end.mutate()}>{t('End session')}</DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
          {conv.isPending ? (
            <div className="p-4">
              <LoadingRows rows={3} />
            </div>
          ) : conv.isError ? (
            <div className="p-4">
              <ErrorState error={conv.error} onRetry={() => void conv.refetch()} />
            </div>
          ) : (
            <ConversationThread
              key={conversationId}
              className="flex-1"
              conv={detail}
              agentName={lead}
              workspaceId={desk.workspaceId}
              live={live}
              stepsUnderReplies
              placeholder={t('Tell {lead} how a case is solved, or what to do next … (Enter to send)', { lead })}
              banner={desk.leadAgent && <ModelOnboarding agentId={desk.leadAgent.agentId} workspaceId={desk.workspaceId} failedText={failedMessage(detail)?.content} />}
            />
          )}
        </div>
        <aside className="flex max-h-[40%] shrink-0 flex-col gap-5 overflow-y-auto border-t bg-muted/20 p-4 max-md:hidden lg:max-h-none lg:w-80 lg:border-t-0 lg:border-l">
          {training ? <TrainingProgress desk={desk} training={training} /> : <LoadingRows rows={4} />}
        </aside>
      </Card>
      {training && (
        <Card className="flex flex-col gap-5 p-4 md:hidden">
          <TrainingProgress desk={desk} training={training} />
        </Card>
      )}
      <TrainingHistory desk={desk} sessions={training?.sessions ?? []} />
    </div>
  );
}

/** The ended sessions and what the desk learned, side by side. */
function TrainingHistory({ desk, sessions }: { desk: DeskDetail; sessions: TrainingSession[] }) {
  if (!sessions.length) return <DeskLearningsSection desk={desk} />;
  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <TrainingSessions desk={desk} sessions={sessions} />
      <DeskLearningsSection desk={desk} />
    </div>
  );
}

/** Between two sessions: where you left off, and "Let's go" for the next one. */
function TrainingBetween({ desk, training }: { desk: DeskDetail; training: TrainingState }) {
  const lead = desk.leadAgent?.name ?? t('the Lead');
  const last = training.sessions[0];
  const start = useApiMutation(() => api.desks.startTraining(desk.id), {
    invalidate: [qk.desk(desk.id), qk.deskTraining(desk.id)],
    success: t('{lead} starts the training', { lead }),
  });
  return (
    <div className="flex flex-col gap-6">
      <div className="grid gap-6 lg:grid-cols-3">
        <Card className="flex min-w-0 flex-col gap-4 p-5 lg:col-span-2">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex size-10 shrink-0 items-center justify-center rounded-full bg-sky-100 text-sky-700 dark:bg-sky-950 dark:text-sky-300">
              <GraduationCap className="size-5" />
            </div>
            <div className="min-w-0 flex-1">
              <h2 className="text-base font-semibold">{t('Start session {n}', { n: training.sessions.length + 1 })}</h2>
              <p className="text-xs text-muted-foreground">{t('{lead} picks up where you left off.', { lead })}</p>
            </div>
            <Button onClick={() => start.mutate()} loading={start.isPending} disabled={!desk.leadAgent || desk.status !== 'active'}>
              <Rocket /> {t('Let’s go')}
            </Button>
          </div>
          {last && (
            <div className="rounded-lg border bg-muted/30 p-4">
              <div className="mb-2 flex flex-wrap items-baseline gap-x-2 text-xs text-muted-foreground">
                <span className="font-semibold tracking-wide uppercase">{t('Where you left off')}</span>
                <span>
                  {last.title} · {sessionDates(last)}
                </span>
              </div>
              {last.summary?.trim() ? (
                <Markdown text={last.summary} className="text-sm" />
              ) : (
                <p className="text-sm text-muted-foreground">{t('This session ended without a summary.')}</p>
              )}
            </div>
          )}
          {training.plan?.trim() && (
            <div>
              <h3 className="mb-1.5 flex items-center gap-1.5 text-xs font-semibold tracking-wide text-muted-foreground uppercase">
                <ListChecks className="size-3.5" /> {t('Training plan')}
              </h3>
              <Markdown text={training.plan} className="gap-2 text-sm" />
            </div>
          )}
        </Card>
        <Card className="flex min-w-0 flex-col gap-5 p-5">
          <ReadinessBlock readiness={training.readiness} godMode={desk.agentMode === 'god'} />
          <AutonomyBlock autonomy={training.autonomy} />
        </Card>
      </div>
      <TrainingCandidatesCard desk={desk} candidates={training.candidates} />
      <TrainingHistory desk={desk} sessions={training.sessions} />
    </div>
  );
}

const sessionDates = (s: TrainingSession) => {
  const day = (at: string) => new Date(at).toLocaleDateString(intlLocale, { day: 'numeric', month: 'short' });
  return day(s.startedAt) === day(s.endedAt) ? day(s.startedAt) : `${day(s.startedAt)} – ${day(s.endedAt)}`;
};

/** The sessions that ended, newest first: each with its summary (open on click) and its chat to read again. */
function TrainingSessions({ desk, sessions }: { desk: DeskDetail; sessions: TrainingSession[] }) {
  const [openId, setOpenId] = useState<string | null>(null);
  const [reading, setReading] = useState<TrainingSession | null>(null);
  return (
    <Section icon={<History />} title={t('Sessions')} description={t('Every training session that ended, with what the Lead summed up.')}>
      <ul className="flex flex-col divide-y">
        {sessions.map((s) => {
          const open = openId === s.id;
          return (
            <li key={s.id} className="py-2 first:pt-0 last:pb-0">
              <div className="flex items-center gap-2">
                <button
                  type="button"
                  onClick={() => setOpenId(open ? null : s.id)}
                  aria-expanded={open}
                  className="flex min-w-0 flex-1 cursor-pointer items-center gap-1.5 text-left text-sm"
                >
                  <ChevronRight className={cn('size-3.5 shrink-0 text-muted-foreground transition-transform', open && 'rotate-90')} />
                  <span className="font-medium">{s.title}</span>
                  <span className="truncate text-xs text-muted-foreground">{sessionDates(s)}</span>
                </button>
                <Button variant="ghost" size="xs" onClick={() => setReading(s)}>
                  {t('Read')}
                </Button>
              </div>
              {open && (
                <div className="mt-1.5 ml-5">
                  {s.summary?.trim() ? (
                    <Markdown text={s.summary} className="gap-2 text-[13px]" />
                  ) : (
                    <p className="text-xs text-muted-foreground">{t('This session ended without a summary.')}</p>
                  )}
                </div>
              )}
            </li>
          );
        })}
      </ul>
      <Dialog open={!!reading} onOpenChange={(o) => !o && setReading(null)}>
        <DialogContent className="flex h-[min(860px,90vh)] flex-col gap-3 p-0 sm:max-w-3xl">
          <DialogHeader className="border-b px-5 pt-5 pb-3">
            <DialogTitle>{reading?.title}</DialogTitle>
            <DialogDescription>{reading ? tr('Ended {date}. Read only.', { date: sessionDates(reading) }) : null}</DialogDescription>
          </DialogHeader>
          {reading && <SessionReader desk={desk} conversationId={reading.id} />}
        </DialogContent>
      </Dialog>
    </Section>
  );
}

const READ_ONLY: ConversationLive = { activity: null, awaitingReply: false, markSent: () => {} };

/** An ended session's chat, read only, with every step the Lead took. */
function SessionReader({ desk, conversationId }: { desk: DeskDetail; conversationId: string }) {
  const conv = useQuery({ queryKey: qk.conversation(conversationId), queryFn: () => api.conversations.get(conversationId) });
  if (conv.isPending) {
    return (
      <div className="px-5">
        <LoadingRows rows={4} />
      </div>
    );
  }
  if (conv.isError) {
    return (
      <div className="px-5">
        <ErrorState error={conv.error} onRetry={() => void conv.refetch()} />
      </div>
    );
  }
  return (
    <ConversationThread
      className="min-h-0 flex-1"
      conv={conv.data}
      agentName={conv.data.agentName ?? desk.leadAgent?.name ?? t('the Lead')}
      workspaceId={desk.workspaceId}
      live={READ_ONLY}
      stepsUnderReplies
      disabledNotice={<p className="py-1 text-center text-sm text-muted-foreground">{t('This session ended.')}</p>}
    />
  );
}

/** Beside the chat: the stage and what's missing, the kinds of work trained, the autonomy KPI and the Lead's plan. */
function TrainingProgress({ desk, training }: { desk: DeskDetail; training: TrainingState }) {
  return (
    <>
      {training.inTraining.length > 0 && (
        <SideBlock icon={<GraduationCap />} title={t('In this session')}>
          <ul className="flex flex-col gap-1.5">
            {training.inTraining.map((c) => (
              <li key={c.id} className="flex min-w-0 items-center gap-2 text-sm">
                <Link
                  to="/w/$workspaceId/work-items/$workItemId"
                  params={{ workspaceId: desk.workspaceId, workItemId: c.id }}
                  className="min-w-0 flex-1 truncate font-medium hover:text-primary hover:underline max-md:py-1.5"
                >
                  {c.title}
                </Link>
                {c.externalReference && <span className="shrink-0 font-mono text-xs text-muted-foreground">{c.externalReference.id}</span>}
              </li>
            ))}
          </ul>
        </SideBlock>
      )}
      <ReadinessBlock readiness={training.readiness} godMode={desk.agentMode === 'god'} />
      {training.candidates.length > 0 && (
        <SideBlock icon={<Inbox />} title={t('Open work to train on')}>
          <TrainingCandidates desk={desk} candidates={training.candidates} compact />
        </SideBlock>
      )}
      <SideBlock icon={<Tags />} title={t('Kinds of work')}>
        {training.kinds.length === 0 ? (
          <p className="text-xs text-muted-foreground">{t('No case worked yet. The Lead records every case you go through together.')}</p>
        ) : (
          <table className="w-full text-xs">
            <thead className="text-muted-foreground">
              <tr>
                <th className="pb-1 text-left font-normal">{t('Kind')}</th>
                <th className="pb-1 text-right font-normal" title={t('Cases worked in the training')}>
                  {t('Cases')}
                </th>
                <th className="pb-1 text-right font-normal" title={t('Cases you had to correct or show the way')}>
                  {t('Corrected')}
                </th>
                <th className="pb-1 text-right font-normal" title={t('The latest cases in a row that needed no correction')}>
                  {t('Clean')}
                </th>
              </tr>
            </thead>
            <tbody>
              {training.kinds.map((k) => (
                <tr key={k.kind} className="border-t">
                  <td className="py-1 pr-2 font-medium">{k.kind}</td>
                  <td className="py-1 text-right tabular-nums">{k.cases}</td>
                  <td className="py-1 text-right tabular-nums">{k.corrected}</td>
                  <td className={cn('py-1 text-right tabular-nums', k.cleanInARow >= 3 && 'font-medium text-emerald-700 dark:text-emerald-300')}>
                    {k.cleanInARow}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </SideBlock>
      <AutonomyBlock autonomy={training.autonomy} />
      <SideBlock icon={<ListChecks />} title={t('Training plan')}>
        {training.plan?.trim() ? (
          <Markdown text={training.plan} className="gap-2 text-xs" />
        ) : (
          <p className="text-xs text-muted-foreground">{t('The Lead writes its plan once it knows the desk’s work.')}</p>
        )}
      </SideBlock>
    </>
  );
}

function SideBlock({ icon, title, children }: { icon: ReactNode; title: string; children: ReactNode }) {
  return (
    <section>
      <h3 className="mb-2 flex items-center gap-1.5 text-xs font-semibold tracking-wide text-muted-foreground uppercase [&_svg]:size-3.5">
        {icon} {title}
      </h3>
      {children}
    </section>
  );
}

/** The stage, and what Deskworks still misses before the next one (it measures that, not the Lead). */
function ReadinessBlock({ readiness, godMode }: { readiness: StageReadiness; godMode?: boolean }) {
  const next = readiness.next ? DESK_STAGE_INFO[readiness.next].label : null;
  return (
    <SideBlock icon={<Rocket />} title={t('Stage')}>
      <div className="flex flex-wrap items-center gap-2 text-xs">
        <StageBadge stage={readiness.stage} />
        {next && (
          <>
            <span className="text-muted-foreground">→</span>
            <span className="font-medium">{next}</span>
          </>
        )}
      </div>
      {!next ? (
        <p className="mt-2 text-xs text-muted-foreground">
          {godMode ? t('The last stage, in God mode: the desk decides on its own.') : t('The last stage. Once it runs steadily, the Lead may propose God mode.')}
        </p>
      ) : readiness.ready ? (
        <p className="mt-2 flex items-center gap-1.5 text-xs font-medium text-emerald-700 dark:text-emerald-300">
          <Check className="size-3.5" /> {t('Ready for {stage} — the Lead proposes it.', { stage: next })}
        </p>
      ) : (
        <>
          <p className="mt-2 text-xs text-muted-foreground">{t('Still missing for {stage}:', { stage: next })}</p>
          <ul className="mt-1 flex flex-col gap-1 text-xs">
            {readiness.missing.map((m) => (
              <li key={m} className="flex gap-1.5">
                <CircleDashed className="mt-0.5 size-3 shrink-0 text-muted-foreground" />
                <span>{m}</span>
              </li>
            ))}
          </ul>
        </>
      )}
      {readiness.keptUntil && (
        <p className="mt-2 text-xs text-muted-foreground">
          {t('You kept the stage; the Lead proposes it again from {date}.', { date: new Date(readiness.keptUntil).toLocaleDateString(intlLocale) })}
        </p>
      )}
    </SideBlock>
  );
}

const rateText = (rate: number) => rate.toLocaleString(intlLocale, { maximumFractionDigits: 1, minimumFractionDigits: 1 });

/** The autonomy KPI: interventions (questions + corrections) per piece of work, its trend, and the last weeks. */
export function AutonomyBlock({ autonomy }: { autonomy: Autonomy }) {
  return (
    <SideBlock icon={<Gauge />} title={t('Autonomy')}>
      <AutonomyFigure autonomy={autonomy} />
      <AutonomyWeeks autonomy={autonomy} />
    </SideBlock>
  );
}

/** "0.4 interventions per piece of work ↓" — lower is better. */
export function AutonomyFigure({ autonomy, compact }: { autonomy: Autonomy; compact?: boolean }) {
  if (autonomy.rate === null) {
    return <p className="text-xs text-muted-foreground">{t('No work in the last 14 days yet.')}</p>;
  }
  const trend = autonomy.previousRate === null ? 0 : autonomy.rate - autonomy.previousRate;
  const better = trend < 0;
  return (
    <div className={cn('flex flex-wrap items-baseline gap-x-1.5', compact ? 'text-sm' : '')}>
      <span className={cn('font-semibold tabular-nums', compact ? 'text-sm' : 'text-2xl')}>{rateText(autonomy.rate)}</span>
      <span className="text-xs text-muted-foreground">{t('interventions per piece of work (14 days)')}</span>
      {trend !== 0 && autonomy.previousRate !== null && (
        <span
          className={cn('inline-flex items-center text-xs font-medium', better ? 'text-emerald-700 dark:text-emerald-300' : 'text-amber-700 dark:text-amber-300')}
          title={t('before: {rate}', { rate: rateText(autonomy.previousRate) })}
        >
          {better ? <ArrowDownRight className="size-3.5" /> : <ArrowUpRight className="size-3.5" />}
          {rateText(Math.abs(trend))}
        </span>
      )}
    </div>
  );
}

/** Work and interventions per week, as paired bars. */
function AutonomyWeeks({ autonomy }: { autonomy: Autonomy }) {
  const weeks = autonomy.weeks.slice(-8);
  const max = Math.max(1, ...weeks.map((w) => Math.max(w.work, w.questions + w.corrections)));
  if (!weeks.some((w) => w.work || w.questions || w.corrections)) return null;
  return (
    <div className="mt-3">
      <div className="flex h-16 items-end gap-1.5" role="img" aria-label={t('Work and interventions per week')}>
        {weeks.map((w) => {
          const interventions = w.questions + w.corrections;
          return (
            <div
              key={w.week}
              className="flex h-full flex-1 items-end justify-center gap-0.5"
              title={t('Week of {date}: {work} done, {questions} questions, {corrections} corrections, {approvals} approvals', {
                date: new Date(w.week).toLocaleDateString(intlLocale, { day: 'numeric', month: 'short' }),
                work: w.work,
                questions: w.questions,
                corrections: w.corrections,
                approvals: w.approvals,
              })}
            >
              <span className="w-1/2 max-w-2.5 rounded-t-sm bg-sky-500/80" style={{ height: `${(w.work / max) * 100}%`, minHeight: w.work ? 2 : 0 }} />
              <span
                className="w-1/2 max-w-2.5 rounded-t-sm bg-amber-500/80"
                style={{ height: `${(interventions / max) * 100}%`, minHeight: interventions ? 2 : 0 }}
              />
            </div>
          );
        })}
      </div>
      <div className="mt-1.5 flex gap-3 text-[11px] text-muted-foreground">
        <span className="flex items-center gap-1">
          <span className="size-2 rounded-sm bg-sky-500/80" /> {t('done')}
        </span>
        <span className="flex items-center gap-1">
          <span className="size-2 rounded-sm bg-amber-500/80" /> {t('questions & corrections')}
        </span>
      </div>
    </div>
  );
}
